// The capability gate, frontend side.
//
// One fetch per practice of GET /api/practices/<id>/capabilities, cached for the
// life of the tab, exposing `features` — the map every page reads to decide whether
// it should render at all. Atlas has no router and no state library, so the cache is
// a module-level Map and the hook subscribes to it; a second component asking for the
// same practice reuses the in-flight promise rather than issuing a second request.
//
// CONTRACT: read `features.<key>`. Never branch on emr_system / pm_system /
// clearinghouse in a page — that is the fork the gate exists to prevent.
//
// While the fetch is in flight `loading` is true and every feature reads false, so a
// page renders nothing rather than flashing a surface the practice may not have. On a
// failed fetch `error` is set and features stay false: fail closed, same as the
// backend.

import { useCallback, useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

// practiceId -> {source, capabilities, features, computed, overrides}
const cache = new Map();
// practiceId -> in-flight promise, so concurrent mounts share one request
const inFlight = new Map();
const subscribers = new Set();

function notify() {
  subscribers.forEach((fn) => fn());
}

// Drop the cached answer and fetch a fresh one. Call this after anything that
// changes what a practice's source is — storing EMR or clearinghouse credentials,
// switching a system dropdown — because the gate is computed from exactly those
// columns and would otherwise stay stale until a page reload. It re-fetches rather
// than only invalidating: subscribers cannot trigger a fetch themselves (their
// effect is keyed on practiceId, which has not changed), so a bare invalidate would
// leave every one of them showing nothing.
export function invalidateCapabilities(practiceId) {
  cache.delete(practiceId);
  inFlight.delete(practiceId);
  notify();
  if (practiceId) {
    fetchOnce(practiceId).catch(() => {
      /* subscribers render their own error state; nothing to do here */
    });
  }
}

// Exported for tests and for the Practice Details panel, which knows the new gate
// after a PUT and should not have to re-fetch it.
export function primeCapabilities(practiceId, data) {
  cache.set(practiceId, data);
  inFlight.delete(practiceId);
  notify();
}

async function load(practiceId) {
  const response = await fetch(`${API_URL}/api/practices/${practiceId}/capabilities`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to load capabilities: ${response.status}`);
  }
  return response.json();
}

function fetchOnce(practiceId) {
  if (inFlight.has(practiceId)) return inFlight.get(practiceId);
  const promise = load(practiceId)
    .then((data) => {
      cache.set(practiceId, data);
      inFlight.delete(practiceId);
      notify();
      return data;
    })
    .catch((err) => {
      inFlight.delete(practiceId);
      throw err;
    });
  inFlight.set(practiceId, promise);
  return promise;
}

export default function useCapabilities(practiceId) {
  const [, forceRender] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fn = () => forceRender((n) => n + 1);
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }, []);

  useEffect(() => {
    if (!practiceId) return;
    if (cache.has(practiceId)) return;
    let cancelled = false;
    setError(null);
    fetchOnce(practiceId).catch(() => {
      if (!cancelled) setError('Could not load this practice’s features.');
    });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  const refresh = useCallback(async () => {
    if (!practiceId) return null;
    invalidateCapabilities(practiceId);
    try {
      return await fetchOnce(practiceId);
    } catch (err) {
      setError('Could not load this practice’s features.');
      return null;
    }
  }, [practiceId]);

  const data = practiceId ? cache.get(practiceId) : null;
  return {
    features: (data && data.features) || {},
    capabilities: (data && data.capabilities) || [],
    source: (data && data.source) || null,
    computed: (data && data.computed) || {},
    overrides: (data && data.overrides) || {},
    // {has_rows, sync_available}: whether the appointments table has rows for this
    // practice, or a sync could fill it. Not a feature — it is a question about the
    // data, and the sidebar's Appointments tab is the one reader.
    appointments: (data && data.appointments) || null,
    loading: !!practiceId && !data && !error,
    error,
    refresh,
  };
}
